import React from "react";
import BusinessAppSettingsCloseButton from "./BusinessAppSettingsCloseButton";
import BusinessSidebarRailToggleIcon from "./BusinessSidebarRailToggleIcon";

/**
 * True when running inside the macOS Electron shell (hiddenInset titlebar).
 *
 * @example
 * isElectronDarwin() // true in Electron on Mac; false in browser
 */
const isElectronDarwin = () =>
  Boolean(
    typeof window !== "undefined" &&
      window.starmeshDesktop?.isElectron &&
      window.starmeshDesktop?.platform === "darwin"
  );

/**
 * Top-level settings chrome: panel toggle (same placement as main tasks) then
 * back/home, overlaid so the settings rail can use full height.
 *
 * On macOS Electron, a flex drag fill sits after the controls so the window
 * stays draggable without a full-width drag overlay stealing button clicks.
 *
 * @example
 * <BusinessAppSettingsChrome
 *   isNavCollapsed={false}
 *   onToggleNav={() => setCollapsed((v) => !v)}
 *   onBackHome={() => navigate("/business/action-items")}
 * />
 *
 * @param {object} props
 * @param {boolean} props.isNavCollapsed
 * @param {() => void} props.onToggleNav
 * @param {() => void} props.onBackHome
 */
const BusinessAppSettingsChrome = ({
  isNavCollapsed,
  onToggleNav,
  onBackHome,
}) => {
  const electronDarwin = isElectronDarwin();

  /*
   * On macOS Electron the back-home pill is rendered as a separate
   * position:fixed element at a known pixel coordinate — completely outside
   * the chrome header's flex/stacking context and any drag-region geometry.
   * This guarantees it is always on top and always receives pointer events,
   * regardless of z-index stacking or webkit-app-region paint order.
   *
   * Layout math (matches BusinessMode.css):
   *   trafficLightPosition.x = 16, lights ≈ 52px wide
   *   toggle left = 78px, toggle width = 28px, gap = 8px
   *   → pill left = 78 + 28 + 8 = 114px
   *   top = 12px (traffic-adjacent padding-top)
   */
  if (electronDarwin) {
    return (
      <>
        <header className="business-app-settings-chrome business-app-settings-chrome--traffic-adjacent">
          <button
            type="button"
            className="business-sidebar-panel-toggle business-app-settings-panel-toggle"
            onClick={onToggleNav}
            aria-label={
              isNavCollapsed
                ? "Expand settings sidebar"
                : "Collapse settings sidebar"
            }
            aria-expanded={!isNavCollapsed}
            title={isNavCollapsed ? "Show sidebar" : "Hide sidebar"}
          >
            <BusinessSidebarRailToggleIcon collapsed={isNavCollapsed} />
          </button>
          <div className="business-app-settings-chrome__drag-fill" aria-hidden="true" />
        </header>
        {/* Render back-home pill outside the chrome header — fully isolated fixed element */}
        <BusinessAppSettingsCloseButton
          onClick={onBackHome}
          extraStyle={{
            position: "fixed",
            top: 14,
            left: 114,
            zIndex: 9999,
            WebkitAppRegion: "no-drag",
          }}
        />
      </>
    );
  }

  return (
    <header className="business-app-settings-chrome">
      <button
        type="button"
        className="business-sidebar-panel-toggle business-app-settings-panel-toggle"
        onClick={onToggleNav}
        aria-label={
          isNavCollapsed
            ? "Expand settings sidebar"
            : "Collapse settings sidebar"
        }
        aria-expanded={!isNavCollapsed}
        title={isNavCollapsed ? "Show sidebar" : "Hide sidebar"}
      >
        <BusinessSidebarRailToggleIcon collapsed={isNavCollapsed} />
      </button>
      <BusinessAppSettingsCloseButton onClick={onBackHome} />
    </header>
  );
};

export default BusinessAppSettingsChrome;
